import { find } from "lodash";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  InputAdornment,
  Box,
  Card,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import {
  linkAccountToUser,
  fetchUserAccount,
  fetchUserCards,
  syncMyPayments,
} from "./../slices/user";
import LinkAccountDialog from "./LinkAccountDialog";
import PaymentDialog from "./PaymentDialog";

import {
  subscribeCustomerPayments,
  unsubscribeCustomerPayments,
} from "./../slices/payments";
import { canopy } from "./../theme";
import { formatCents, dollarsToCents, netBalanceCents } from "../utils/money";
import { paymentsNewestFirst } from "../utils/payments";


const IconTile = ({ children }) => (
  <Box
    sx={{
      width: 44,
      height: 44,
      borderRadius: "14px",
      bgcolor: canopy.soft,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: canopy.green,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const ActionRow = ({ icon, title, subtitle, onClick, endContent }) => (
  <Card
    onClick={onClick}
    sx={{
      p: "18px 20px",
      display: "flex",
      alignItems: "center",
      gap: 1.75,
      cursor: onClick ? "pointer" : "default",
    }}
  >
    <IconTile>{icon}</IconTile>
    <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={0.25}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
        {subtitle}
      </Typography>
    </Stack>
    {endContent || <ChevronRightIcon sx={{ color: canopy.green }} />}
  </Card>
);

const AutopayRow = ({ onEnroll }) => {
  const navigate = useNavigate();
  const { loading, subscriptions } = useSelector((state) => state.user);

  const autopayOn = React.useMemo(
    () => find(subscriptions, (s) => s.status === "ACTIVE"),
    [subscriptions],
  );
  const autopayPending = React.useMemo(
    () => find(subscriptions, (s) => s.status === "PENDING"),
    [subscriptions],
  );
  const autopayCancelled = React.useMemo(
    () => find(subscriptions, (s) => s.status === "PENDING" && s.canceledDate),
    [subscriptions],
  );

  if (loading) {
    return (
      <ActionRow
        icon={<AutorenewIcon />}
        title="Autopay"
        subtitle="Checking your autopay status…"
        endContent={<CircularProgress size={20} sx={{ color: canopy.green }} />}
      />
    );
  }

  if (autopayOn || (autopayPending && !autopayCancelled)) {
    return (
      <ActionRow
        icon={<AutorenewIcon />}
        title={autopayOn ? "Autopay is on" : "Autopay is pending"}
        subtitle="$15 on the 1st, every month · manage in profile"
        onClick={() => navigate("/profile")}
        endContent={
          <Chip
            label={autopayOn ? "ACTIVE" : "PENDING"}
            size="small"
            sx={{
              bgcolor: autopayOn ? canopy.soft : canopy.warnSoft,
              color: autopayOn ? canopy.greenDark : canopy.warn,
              fontSize: 11,
            }}
          />
        }
      />
    );
  }

  return (
    <ActionRow
      icon={<AutorenewIcon />}
      title={autopayCancelled ? "Restart autopay" : "Turn on autopay"}
      subtitle="$15 on the 1st, every month"
      onClick={onEnroll}
    />
  );
};

const SavedCardsRow = () => {
  const navigate = useNavigate();
  const { savedCards } = useSelector((state) => state.user);
  const firstCard = savedCards?.[0];
  return (
    <ActionRow
      icon={<CreditCardIcon />}
      title="Saved cards"
      subtitle={
        firstCard
          ? `${firstCard.cardBrand} ending in ${firstCard.last4}${
              savedCards.length > 1 ? ` +${savedCards.length - 1} more` : ""
            }`
          : "Manage your payment methods"
      }
      onClick={() => navigate("/profile")}
    />
  );
};

const UserPage = () => {
  const dispatch = useDispatch();
  const [note, setNote] = useState(null);
  const [customAmount, setCustomAmount] = useState(0);
  const [customOpen, setCustomOpen] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { linkedAccount } = useSelector((state) => state.user);
  const { payments } = useSelector((state) => state.payments);
  const [dialogOpen, setDialogOpen] = useState(false);
  const uid = user?.uid;
  const squareCustomerId = user?.squareCustomerId;

  // Newest first for the history list
  const history = useMemo(() => paymentsNewestFirst(payments), [payments]);

  useEffect(() => {
    if (uid) dispatch(fetchUserAccount(uid));
  }, [dispatch, uid]);

  useEffect(() => {
    if (!uid) return undefined;
    // Live feed of this resident's Square payments, plus a background sync
    // so anything paid outside the app shows up too.
    dispatch(subscribeCustomerPayments(squareCustomerId));
    if (squareCustomerId) {
      dispatch(syncMyPayments());
      dispatch(fetchUserCards(squareCustomerId));
    }
    return () => dispatch(unsubscribeCustomerPayments());
  }, [dispatch, uid, squareCustomerId]);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);
  const handleLinkAccount = (accountId) => {
    dispatch(linkAccountToUser({ userId: user.uid, accountId }));
    handleCloseDialog();
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const amountDue = linkedAccount?.amountDue || 0;
  const credit = linkedAccount?.credit || 0;
  const netBalance = netBalanceCents(linkedAccount);
  const owes = amountDue > 0 && netBalance < 0;
  const firstName = user?.firstName;

  const openCustom = () => setCustomOpen(true);
  const closeCustom = () => setCustomOpen(false);
  const submitCustom = () => {
    closeCustom();
    setNote("Custom Amount");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 10 }}>
      <Box
        sx={{
          bgcolor: canopy.soft,
          borderRadius: "0 0 28px 28px",
          px: 3,
          pt: 3,
          pb: 3.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.25,
          maxWidth: (t) => t.breakpoints.values.md,
          mx: "auto",
        }}
      >
        {firstName && (
          <Typography variant="h6" sx={{ color: canopy.greenDark }}>
            Welcome, {firstName}
          </Typography>
        )}
        {linkedAccount ? (
          <>
            <Stack spacing={0.75}>
              <Typography sx={{ color: canopy.inkSoft, fontWeight: 600 }}>
                Your balance
              </Typography>
              <Typography
                sx={{ fontSize: 46, fontWeight: 800, lineHeight: 1, color: "text.primary" }}
              >
                {owes ? formatCents(amountDue) : formatCents(Math.max(netBalance, 0))}
              </Typography>
              <Typography sx={{ color: canopy.inkSoft, fontWeight: 600, fontSize: 14 }}>
                {owes
                  ? credit > 0
                    ? `Amount due · includes ${formatCents(credit)} credit applied`
                    : "Amount due on your account"
                  : netBalance > 0
                    ? "Credit on your account — you're all caught up"
                    : "You're all caught up — thank you!"}
              </Typography>
            </Stack>
            {owes ? (
              <Stack spacing={1.25}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setNote("Single Payment")}
                >
                  Pay {formatCents(amountDue)}
                </Button>
                <Button
                  onClick={openCustom}
                  sx={{ color: canopy.green, fontWeight: 700, alignSelf: "center" }}
                >
                  Pay a custom amount
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: 54,
                    px: 4,
                    bgcolor: "background.paper",
                    color: canopy.greenDark,
                    borderRadius: "27px",
                    fontWeight: 700,
                  }}
                >
                  <CheckIcon sx={{ color: canopy.green }} />
                  Payments up to date
                </Box>
              </Stack>
            )}
          </>
        ) : (
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography variant="h6">No account linked</Typography>
            <Typography sx={{ color: canopy.inkSoft, fontWeight: 600, fontSize: 14 }}>
              Link your property to see your balance and pay dues.
            </Typography>
            <Button variant="contained" size="large" onClick={handleOpenDialog}>
              Link account
            </Button>
          </Stack>
        )}
      </Box>

      <Stack
        spacing={2}
        sx={{ px: 3, pt: 2.5, maxWidth: (t) => t.breakpoints.values.md, mx: "auto" }}
      >
        {linkedAccount && (
          <>
            <AutopayRow onEnroll={() => setNote("Monthly Autopay")} />
            <SavedCardsRow />
          </>
        )}

        <Stack spacing={1.25}>
          <Typography sx={{ fontWeight: 800 }}>Payment history</Typography>
          <Card sx={{ px: 2.5, py: 0.75 }}>
            {history.length ? (
              history.map((transaction, index) => (
                <Box
                  key={transaction.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    py: 1.75,
                    borderBottom:
                      index < history.length - 1
                        ? `1px solid ${canopy.hairline}`
                        : "none",
                  }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }} noWrap>
                      {transaction.note || "Payment"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}
                    >
                      {formatDate(transaction.createdAt)}
                    </Typography>
                  </Stack>
                  <Stack alignItems="flex-end" spacing={0.25}>
                    <Typography sx={{ fontWeight: 800, fontSize: 15, color: canopy.green }}>
                      {formatCents(transaction.amountMoney?.amount)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color:
                          transaction.status === "COMPLETED" ? canopy.green : canopy.warn,
                      }}
                    >
                      {transaction.status}
                    </Typography>
                  </Stack>
                </Box>
              ))
            ) : (
              <Typography
                sx={{ py: 3, textAlign: "center", color: "text.secondary", fontWeight: 600 }}
              >
                No payments yet.
              </Typography>
            )}
          </Card>
        </Stack>
      </Stack>

      <LinkAccountDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onLink={handleLinkAccount}
      />

      <Dialog open={customOpen} onClose={closeCustom} fullWidth maxWidth="xs">
        <DialogTitle>Pay a custom amount</DialogTitle>
        <DialogContent>
          <TextField
            value={customAmount}
            type="number"
            fullWidth
            autoFocus
            onChange={(e) => setCustomAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ step: "0.01" }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCustom}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!(customAmount > 0)}
            onClick={submitCustom}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentDialog
        paymentAmount={
          customAmount > 0 && note === "Custom Amount"
            ? dollarsToCents(customAmount)
            : amountDue
        }
        note={note}
        onClose={() => setNote(null)}
        amountDue={amountDue}
      />
    </Box>
  );
};

export default UserPage;
